import { useAuthStore } from '@/shared/store/authStore';

const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// Format an ISO-ish date (YYYY-MM-DD, full ISO timestamp, or Date-parseable
// string) using one of the company-wide date_format options. Pure function
// — no React dependency, so it works in component render, PDF renderers,
// and utility modules.
export function formatDate(input: string | Date | null | undefined, format = 'MM/DD/YYYY'): string {
  if (!input) return '';
  const d = input instanceof Date ? input : parseLoose(input);
  if (!d || Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  const mShort = MONTH_SHORT[m - 1];
  switch (format) {
    case 'MM/DD/YYYY':   return `${pad(m)}/${pad(day)}/${y}`;
    case 'DD/MM/YYYY':   return `${pad(day)}/${pad(m)}/${y}`;
    case 'YYYY-MM-DD':   return `${y}-${pad(m)}-${pad(day)}`;
    case 'DD.MM.YYYY':   return `${pad(day)}.${pad(m)}.${y}`;
    case 'MMM D, YYYY':  return `${mShort} ${day}, ${y}`;
    case 'D MMM YYYY':   return `${day} ${mShort} ${y}`;
    default:             return `${pad(m)}/${pad(day)}/${y}`;
  }
}

// Accept "2026-04-22" or "2026-04-22T00:00:00Z" or similar. Construct a
// local-time Date for bare YYYY-MM-DD (avoids the UTC-midnight → prev-day
// surprise), and let the built-in parser handle full ISO timestamps.
function parseLoose(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

// React hook — pulls the active company's date_format from auth state.
// Use in components: `const fmt = useDateFormat(); fmt(inv.date)`.
export function useDateFormat(): (input: string | Date | null | undefined) => string {
  const format = useAuthStore((s) => s.company?.date_format || 'MM/DD/YYYY');
  return (input) => formatDate(input, format);
}

// The company date format followed by the time of day, 12-hour with am/pm,
// as the document bottom bars (CreatedByLine) and the audit trail show it:
// "10/08/2026 2:05pm". For a timestamp, never for a bare date.
export function formatDateTime(input: string | Date | null | undefined, format = 'MM/DD/YYYY'): string {
  if (!input) return '';
  const d = input instanceof Date ? input : parseLoose(input);
  if (!d || Number.isNaN(d.getTime())) return '';
  const h24 = d.getHours();
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${formatDate(d, format)} ${h12}:${String(d.getMinutes()).padStart(2, '0')}${h24 < 12 ? 'am' : 'pm'}`;
}

// React hook twin of formatDateTime: the active company's date_format.
export function useDateTimeFormat(): (input: string | Date | null | undefined) => string {
  const format = useAuthStore((s) => s.company?.date_format || 'MM/DD/YYYY');
  return (input) => formatDateTime(input, format);
}
